import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../util/constants";
import {RootState, useAppSelector} from "../../../store";
import Stack from "@mui/material/Stack";
import {alpha, useTheme} from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {ReactNode} from "react";

interface ICtaItem {
    title: string
    buttonLabel: string
    to: string
    icon: ReactNode
}

const LetsBegin = () => {
    const {t} = useTranslation()
    const theme = useTheme()
    const isLoggedIn = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.id)
    const promoPoints = useAppSelector((state: RootState) => state.appReducer.featureToggles.promo_points)
    const ctaItems: ICtaItem[] = [
        {
            title: isLoggedIn ? t('start_wishing') : !promoPoints.value
                ? t('register_now_and_start_wishing')
                : t('start_and_free_points', {points: promoPoints.points}),
            buttonLabel: isLoggedIn ? t('wishes') : t('register'),
            to: !isLoggedIn ? RoutePaths.AUTH : RoutePaths.WISHES,
            icon: <AutoAwesomeOutlinedIcon fontSize="large"/>,
        },
        {
            title: t('learn_more_rules'),
            buttonLabel: t('terms'),
            to: RoutePaths.TERMS,
            icon: <GavelOutlinedIcon fontSize="large"/>,
        },
        {
            title: t('discover_guide_text'),
            buttonLabel: t('guide'),
            to: RoutePaths.GUIDE,
            icon: <MenuBookOutlinedIcon fontSize="large"/>,
        },
    ]

    return (
        <Box textAlign="center" mt={8} sx={{px: {xs: 0, sm: 4}}}>
            <Typography variant="h4" fontWeight={600} color="primary">
                {t('lets_begin')}
            </Typography>
            <Box
                maxWidth={1120}
                mx="auto"
                mt={3}
                sx={{
                    borderRadius: {xs: "28px", md: "36px"},
                    background: `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.12)} 0%, ${alpha(theme.palette.common.white, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                    backdropFilter: "blur(12px)",
                    boxShadow: `0 24px 60px ${alpha(theme.palette.common.black, 0.16)}`,
                    overflow: "hidden",
                }}
            >
                <Grid container>
                    {ctaItems.map((item, index) => (
                        <Grid
                            key={item.to}
                            size={index === 2 ? {xs: 12, sm: 12, lg: 4} : {xs: 12, sm: 6, lg: 4}}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                borderLeft: {
                                    xs: "none",
                                    sm: index === 1 ? `1px solid ${alpha(theme.palette.common.white, 0.1)}` : "none",
                                    lg: index === 0 ? "none" : `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                                },
                                borderTop: {
                                    xs: index === 0 ? "none" : `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
                                    sm: index === 2 ? `1px solid ${alpha(theme.palette.common.white, 0.1)}` : "none",
                                    lg: "none",
                                },
                            }}
                        >
                            <Stack
                                spacing={2}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    width: "100%",
                                    maxWidth: {xs: 320, sm: index === 2 ? 420 : 320, lg: 320},
                                    minHeight: {xs: "auto", lg: 230},
                                    px: {xs: 3, sm: 4},
                                    py: {xs: 3.5, lg: 4.5},
                                    textAlign: "center",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: "18px",
                                        flexShrink: 0,
                                        display: "grid",
                                        placeItems: "center",
                                        color: alpha(theme.palette.common.white, 0.95),
                                        backgroundColor: alpha(theme.palette.common.white, 0.12),
                                        border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                                    }}
                                >
                                    {item.icon}
                                </Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={600}
                                    color="primary"
                                    sx={{
                                        fontSize: {xs: "1.2rem", sm: "1.5rem"},
                                        maxWidth: {xs: 280, sm: index === 2 ? 360 : 280, lg: 280},
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Button
                                    variant="contained"
                                    component={RouterLink}
                                    to={item.to}
                                    sx={{
                                        minWidth: {xs: 210, sm: 220},
                                        flexShrink: 0,
                                    }}
                                >
                                    {item.buttonLabel}
                                </Button>
                            </Stack>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
}

export default LetsBegin
